import { Box } from '@mui/material';
import React, { useEffect, useState } from 'react';
import useEmployeeData from 'containers/pages/timesheets/hooks/useEmployeeData';
import TimesheetSelectors from 'containers/pages/timesheets/components/selectors/TimesheetSelectors';
import TimesheetsSummaryCard from 'containers/pages/timesheets/components/summaryCard/TimesheetsSummaryCard';
import TimesheetsToolbar from 'containers/pages/timesheets/components/toolbar/TimesheetsToolbar';
import LoadingBackdrop from 'components/LoadingBackdrop';
import useComments from 'containers/pages/comments/hooks/useComments';
import { useNotificationProvider } from 'hooks';
import CaseCommentsTable from 'containers/pages/comments/components/caseTable/CaseCommentsTable';
import CaseCommentManager from 'containers/pages/comments/components/caseTable/CaseCommentManager';
import { CANtrackComment } from 'containers/pages/comments/types/comment';

const TimesheetsRich: React.FC = () => {
	const {
		employees,
		selectedEmployee,
		loadingEmployees,
		setSelectedEmployee,
	} = useEmployeeData();

	const [selectedDate, setSelectedDate] = useState<Date>(new Date());
	const {
		comments: timesheetEntries,
		loadingComments: loadingTimesheetEntries,
		fetchComments,
		setComments,
	} = useComments();
	const { notifyError } = useNotificationProvider();

	const [searchTerm, setSearchTerm] = useState<string>('');

	useEffect(() => {
		if (selectedEmployee) {
			fetchEntriesForTimesheets();
		}
	}, [selectedEmployee, selectedDate]);

	const fetchEntriesForTimesheets = async () => {
		if (selectedEmployee) {
			fetchComments({
				employeeId: selectedEmployee.Id,
				date: selectedDate,
			});
		} else {
			setComments([]);
			notifyError('Please select an employee');
		}
	};

	const [decimalFormat, setDecimalFormat] = useState<boolean>(true);
	const [commentToCopy, setCommentToCopy] = useState<CANtrackComment>();

	const handleCopyComment = (comment: CANtrackComment) => {
		setCommentToCopy({
			...comment,
			id: 0,
		});
	};

	const filteredEntries = timesheetEntries.filter((entry) => {
		if (!searchTerm.trim()) return true;
		const term = searchTerm.toLowerCase();
		return (
			entry.customerName?.toLowerCase().includes(term) ||
			entry.caseTitle?.toLowerCase().includes(term) ||
			entry.title?.toLowerCase().includes(term) ||
			entry.description?.toLowerCase().includes(term)
		);
	});

	return (
		<div style={{ padding: '1rem' }}>
			<Box
				sx={{
					display: 'flex',
					flexDirection: { xs: 'column', md: 'row' },
					gap: 1,
					alignItems: { xs: 'stretch', md: 'center' },
				}}
			>
				<TimesheetSelectors
					employees={employees}
					fetchTimesheetEntries={fetchEntriesForTimesheets}
					loadingEmployees={loadingEmployees}
					loadingTimesheetEntries={loadingTimesheetEntries}
					selectedDate={selectedDate}
					setSelectedDate={setSelectedDate}
					selectedEmployee={selectedEmployee}
					setSelectedEmployee={setSelectedEmployee}
				/>
			</Box>
			<Box display="flex" flexDirection={'column'} gap={2}>
				<Box>
					<TimesheetsToolbar
						employeeName={selectedEmployee?.FullName || ''}
						selectedDate={selectedDate}
						decimalFormat={decimalFormat}
						setDecimalFormat={setDecimalFormat}
						loadingEmployees={loadingEmployees}
						loadingTimesheetEntries={loadingTimesheetEntries}
					/>
					<Box display={'flex'} flexDirection={'column'} gap={1}>
						<CaseCommentManager
							setComments={setComments}
							commentToCopy={commentToCopy}
							resetCommentToCopy={() =>
								setCommentToCopy(undefined)
							}
							userId={selectedEmployee?.UserId}
							contactId={selectedEmployee?.ContactId}
							searchTerm={searchTerm}
							onSearchChange={setSearchTerm}
						/>
						<CaseCommentsTable
							comments={filteredEntries}
							loadingComments={loadingTimesheetEntries}
							showDecimalFormat={decimalFormat}
							setComments={setComments}
							onCopyCommentClick={handleCopyComment}
						/>
					</Box>
				</Box>

				<Box display={'flex'} justifyContent={'flex-end'}>
					<TimesheetsSummaryCard
						timesheetEntries={filteredEntries}
						showDecimal={decimalFormat}
						loadingTimesheetEntries={loadingTimesheetEntries}
					/>
				</Box>
			</Box>
			<LoadingBackdrop open={loadingEmployees} />
		</div>
	);
};

export default TimesheetsRich;
